import { GraphQLResult } from '@aws-amplify/api-graphql';
import { logger } from '@jonni/utils/logger';
import { PageProps, graphql, navigate } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React from 'react';
import { useRecoilState } from 'recoil';
import ImageViewer from 'src/components/Steps/ImageViewer';
import Modal from 'src/components/Steps/Modal';
import StepFooter from 'src/components/Steps/StepFooter';
import StepHeader from 'src/components/Steps/StepHeader';
import Uploader from 'src/components/Steps/Uploader';
import { AppendixLink } from 'src/config';
import { API } from 'src/helpers';
import { useLogScreenView } from 'src/helpers/analytics';
import { FILE_KEYS } from 'src/hooks/steps/useSubmitdentificationUpload';
import { Steps, getRoutePath, stepsAtom } from 'src/recoil/steps/atoms';
import { Query } from 'src/shared/graphql/appsync.full';
import {
  StyledForm,
  StyledFormItem,
  StyledLink,
  StyledStepFooterText,
  StyledWrapperStep,
} from 'src/styles/signup/signupSteps';

const getBusinessFileQuery = /* GraphQL */ `
  query GetFile($input: GetBusinessFileInput) {
    getBusinessFile(input: $input) {
      url
    }
  }
`;

async function getFileUrl(businessId: string | null, fileKey: string) {
  const fileRes = (await API.graphql({
    query: getBusinessFileQuery,
    variables: {
      input: {
        businessId,
        path: `documents/${fileKey}`,
      },
    },
  })) as GraphQLResult<{ getBusinessFile: Query['getBusinessFile'] }>;

  return fileRes?.data?.getBusinessFile?.url;
}

const AuthoritiesIdentificationUpload = ({ location: { pathname } }: PageProps) => {
  const [IDCardImageExists, setIDCardImageExists] = React.useState<boolean>(false);
  const [IDAppendixImageExists, setIDAppendixImageExists] = React.useState<boolean>(false);
  const [stepsState, setStepsState] = useRecoilState(stepsAtom);
  const [viewedDocument, setViewedDocument] = React.useState<{ fileName: string; url: string } | null>(null);
  const { t } = useTranslation();
  useLogScreenView(pathname);

  const businessId = stepsState.currentBusinessId;

  const getFileStatus = (fileStatus: boolean, fileKey: string) => {
    if (fileKey === FILE_KEYS.IDCard) {
      setIDCardImageExists(fileStatus);
    } else if (fileKey === FILE_KEYS.IDAppendix) {
      setIDAppendixImageExists(fileStatus);
    }
  };

  const viewDocument = async (docName: string, title: string) => {
    try {
      const fileUrl = await getFileUrl(businessId, docName);

      if (fileUrl) {
        setViewedDocument({ url: fileUrl, fileName: title });
      } else {
        throw { message: 'File does not exist' }; // eslint-disable-line no-throw-literal
      }
    } catch (err) {
      logger.error('Error while downloading documents', {
        error: err,
        businessId,
      });
    }
  };

  const onSubmit = async () => {
    setStepsState({ ...stepsState, currentStep: Steps.BANK_DOCUMENT_UPLOAD });
    await navigate(getRoutePath(Steps.BANK_DOCUMENT_UPLOAD));
  };

  return (
    <>
      <StyledWrapperStep>
        <StepHeader
          title={t('authoritiesIdentification.identificationDetails.title')}
          description={t('authoritiesIdentification.identificationUpload.description')}
        />
        <StyledForm container>
          <StyledFormItem size={{ xs: 12, sm: 6, md: 6 }}>
            <Uploader
              text={'צילום תעודת זהות'}
              businessId={businessId}
              getFileStatus={getFileStatus}
              fileKey={FILE_KEYS.IDCard}
              onClickView={
                () =>
                  /* eslint-disable */
                  viewDocument(FILE_KEYS.IDCard, `צילום תעודת זהות.png`)
                /* eslint-enable */
              }
            />
          </StyledFormItem>

          <StyledFormItem size={{ xs: 12, sm: 6, md: 6 }}>
            <Uploader
              text={'צילום ספח תעודת זהות'}
              businessId={businessId}
              getFileStatus={getFileStatus}
              fileKey={FILE_KEYS.IDAppendix}
              onClickView={
                () =>
                  /* eslint-disable */
                  viewDocument(FILE_KEYS.IDAppendix, ` צילום ספח תעודת זהות.png`)
                /* eslint-enable */
              }
            />
          </StyledFormItem>
        </StyledForm>

        <StyledStepFooterText>
          *במידה ואין ברשותך ספח -{' '}
          <StyledLink href={AppendixLink} target="_blank">
            ניתן להציא ספח דיגטלי מכאן
          </StyledLink>
        </StyledStepFooterText>
      </StyledWrapperStep>
      <StepFooter
        onCancelProps={{ disabled: true, onClick: () => {} }}
        onSubmitProps={{
          disabled: !IDAppendixImageExists || !IDCardImageExists,
          onClick: onSubmit,
        }}
      />

      <Modal
        maxWidth="xl"
        open={!!viewedDocument}
        setIsOpen={() => setViewedDocument(null)}
        messageTitle={viewedDocument ? viewedDocument.fileName.substr(0, viewedDocument.fileName.length - 4) : ''}>
        {viewedDocument ? <ImageViewer imageUrl={viewedDocument.url} /> : <></>}
      </Modal>
    </>
  );
};

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { ns: { in: ["translation"] }, language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

export default AuthoritiesIdentificationUpload;
